import { useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { useHistory } from 'react-router-dom';
import { Layout, Layouts } from 'react-grid-layout';

import { UserDashboard, WidgetItem } from '@vyce/core/src/types';
import { DeviceContext, TeamsDashboardModuleContext, TeamsPageContext } from '@vyce/core/src/contexts';
import { postExportQualifications } from '@vyce/core/src/api/teams';
import { useBooleanState } from '@vyce/core/src/hooks';
import { getFromLS, saveToLS } from '@vyce/core/src/utils/local-storage';
import { sanitizeTeamDashboard } from '@vyce/core/src/views/teamPage/config/constants';

import { NotificationContext } from '../../../contexts/notificationContext';
import { useMainDashboardFilter } from '../../../hooks/useMainDashboardFilter';

const LS_ITEMS_KEY = 'teamDashboardItems';
const LS_LAYOUT_KEY = 'teamDashboardLayouts';

export const useTeamsDashboardData = ({ teamIds }: { teamIds?: string[] }) => {
  const [teamsDashboard, setTeamsDashboard] = useState<UserDashboard>();
  const { handleServerError, showNotification } = useContext(NotificationContext);
  const [buttonLoading, setButtonLoading] = useState<boolean>(false);
  const [isNotifyDialogOpen, openNotifyDialog, closeNotifyDialog] = useBooleanState(false);

  const {
    first_name,
    companyDataLoading,
    teams = [],
    mainDashboardFilters,
  } = useContext(TeamsDashboardModuleContext);

  const { selectedCompanyId = '' } = useContext(TeamsPageContext);

  const { selectedTeamOptions, getTeamsForRequest } = useMainDashboardFilter({
    mainDashboardFilters,
  });

  const history = useHistory();

  const { isMobile } = useContext(DeviceContext);

  const selectedTeamIds = useMemo(
    () => (teamIds ? teamIds : getTeamsForRequest(selectedTeamOptions.length ? 'selected' : 'all')),
    [selectedTeamOptions, teamIds]
  );

  const onWidgetStatusChange = useCallback(
    (systemName: string) => {
      if (teamsDashboard?.widgets?.length === 0) return;

      setTeamsDashboard(value => {
        const newWidgets: WidgetItem[] =
          value?.widgets?.map(item => {
            return item.system_name === systemName ? { ...item, is_active: !item.is_active } : item;
          }) || [];
        saveToLS(LS_ITEMS_KEY, newWidgets);
        return { ...value, widgets: newWidgets };
      });
    },
    [teamsDashboard?.widgets]
  );

  const resetDashboards = () => {
    if (teamsDashboard && teamsDashboard.widgets) {
      const restoredDashboard = teamsDashboard.widgets.map(item => {
        return { ...item, is_active: true };
      });
      setTeamsDashboard(value => ({ ...value, widgets: restoredDashboard }));
      saveToLS(LS_ITEMS_KEY, restoredDashboard);
    }
  };

  const exportQualifications = useCallback(async () => {
    if (!selectedCompanyId) {
      return;
    }
    try {
      setButtonLoading(true);

      await postExportQualifications({
        company_id: selectedCompanyId,
        data: { team_ids: selectedTeamIds, limit: 3000 },
      });
      showNotification({
        message:
          'The Qualification Matrix of your workforce is being prepared. It will be sent to your email address soon!',
        options: { variant: 'info' },
      });
      setButtonLoading(false);
    } catch (e) {
      console.error(e);
      setButtonLoading(false);
      handleServerError(e);
    }
  }, [selectedCompanyId, teams, selectedTeamIds]);

  const onLayoutChange = (currentLayout: Layout[], newLayouts: Layouts) => {
    const newWidgetsSettings = { ...teamsDashboard, layouts: newLayouts };
    setTeamsDashboard(newWidgetsSettings);
    saveToLS(LS_LAYOUT_KEY, newLayouts);
  };

  const onWidgetsChangeViaSettings = (newWidget: WidgetItem[]) => {
    const newWidgetsSettings = { ...teamsDashboard, widgets: newWidget };
    setTeamsDashboard(newWidgetsSettings);
    saveToLS(LS_ITEMS_KEY, newWidget);
  };

  const goToTeamsList = useCallback(() => history.push('/teams/list'), [history]);

  useEffect(() => {
    const savedWidgets = getFromLS(LS_ITEMS_KEY);
    const savedLayouts = getFromLS(LS_LAYOUT_KEY);

    const { items, layouts } = sanitizeTeamDashboard(savedWidgets, savedLayouts);

    saveToLS(LS_ITEMS_KEY, items);
    saveToLS(LS_LAYOUT_KEY, layouts);

    setTeamsDashboard({ widgets: items, layouts });
  }, []);

  return {
    isMobile,
    first_name,
    companyDataLoading,
    teamsDashboard,
    teams,
    selectedCompanyId,
    buttonLoading,
    isNotifyDialogOpen,
    selectedTeamIds,
    onWidgetsChangeViaSettings,
    resetDashboards,
    closeNotifyDialog,
    exportQualifications,
    onWidgetStatusChange,
    onLayoutChange,
    goToTeamsList,
  };
};
